import { first } from 'lodash-es';
import { TYPE_EXTERNAL, TYPE_INTERNAL } from '../AuthType';
import * as Domains from '../domains';
import { EnvironmentProvider, TypeMapping } from '../types';
import { DEV, ENVIRONMENTS, PROD } from './constants';

function externalSubdomainToEnv(subdomain: string) {
  const firstSubdomain = first(Domains.domainToParts(subdomain));
  if (!firstSubdomain) {
    return PROD;
  }
  return ENVIRONMENTS.find((e) => firstSubdomain.endsWith(e)) ?? PROD;
}

function internalSubdomainToEnv(subdomain: string) {
  return ENVIRONMENTS.find((e) => subdomain.endsWith(e)) ?? PROD;
}

/**
 * Return the environment (ex: dev, prod) based on the domain. Assumes that
 * the domain is well known and has the environment embedded in one of its
 * subdomains.
 *
 * For example, the wwwdev.troweprice.com environment is targeting dev. The
 * www.dev.awstrp.net environment is also targeting dev.
 *
 * A type mapping is needed as the environment is determined in a type-specific
 * manner, and the domain is what determines which subdomains to consider.
 *
 * @param fqdn The fully qualified domain name to pull the environment from.
 * @param typeMapping The current type mapping.
 */
export const environmentFromDomain: EnvironmentProvider = (
  fqdn: string,
  typeMapping: TypeMapping,
) => {
  if (Domains.isLocalhost(fqdn)) {
    return DEV;
  }
  const subdomain = Domains.subdomainFromFqdn(fqdn, typeMapping.domain);
  if (subdomain) {
    switch (typeMapping.type) {
      case TYPE_EXTERNAL:
        return externalSubdomainToEnv(subdomain);
      case TYPE_INTERNAL:
        return internalSubdomainToEnv(subdomain);
    }
  }
  return PROD;
};

/**
 * Return the environment (ex: dev, prod) based on the current location. Assumes
 * that the location is an external (troweprice.com) location.
 *
 * @param location
 */
export function environmentFromExternalLocation(location: Location): string {
  return environmentFromDomain(location.hostname, {
    domain: Domains.EXTERNAL_DOMAIN,
    hostRegex: Domains.regexSubdomainsOf(Domains.EXTERNAL_DOMAIN),
    type: 'external',
  });
}
